import React from 'react';
import { MenuItem } from '@mui/material';
import IpdFormField from '../../ipd/utils/IpdFormField';
import IpdTextField from '../../ipd/utils/IpdTextField';
import IpdDateField from '../../ipd/utils/IpdDateField';
import { IPD_COLORS } from '../../ipd/utils/ipdColors';
import { isoToDate, normalizeToIsoDate } from '../../ipd/utils/dateFormat';

export { default as DetailRow } from '../../ipd/utils/IpdDetailRow';

function fieldErrorMessage(error) {
  if (!error) return undefined;
  return error.message || String(error);
}

function withExternalLabel({ label, required, error, helperText, sx, children }) {
  if (!label) return children;
  return (
    <IpdFormField
      label={label}
      required={required}
      error={fieldErrorMessage(error)}
      helperText={helperText}
      sx={sx}
    >
      {children}
    </IpdFormField>
  );
}

// Styled text input — thin wrapper over the shared IpdTextField.
export function InvTextField({
  type,
  value,
  onChange,
  label,
  inputProps,
  InputLabelProps,
  error,
  helperText,
  required,
  sx,
  fullWidth,
  ...props
}) {
  if (type === 'date') {
    // IpdDateField's underlying picker ignores a caller-supplied `inputProps`
    // (it sets its own), so native-input `inputProps={{ min, max }}` range
    // constraints must be translated to IpdDateField's own minDate/maxDate.
    //
    // SILENT CALENDAR BUG: minDate/maxDate only constrain the popup. Clearing
    // the field and typing on the keyboard can still commit a date (sometimes
    // as dd/mm/yyyy). IpdDateField normalizes to yyyy-MM-dd; callers must still
    // validate and show a field error — see IpdDateField.js header comment.
    const { min, max } = inputProps || {};
    const field = (
      <IpdDateField
        {...props}
        value={normalizeToIsoDate(value) || value || ''}
        onChange={(iso) => onChange?.({ target: { value: iso } })}
        minDate={isoToDate(normalizeToIsoDate(min) || min)}
        maxDate={isoToDate(normalizeToIsoDate(max) || max)}
        error={Boolean(error)}
        fullWidth={Boolean(label) || fullWidth}
        sx={label ? undefined : sx}
      />
    );
    return withExternalLabel({ label, required, error, helperText, sx, children: field });
  }
  const field = (
    <IpdTextField
      type={type}
      value={value}
      onChange={onChange}
      hiddenLabel
      inputProps={inputProps}
      InputLabelProps={label ? undefined : InputLabelProps}
      error={Boolean(error)}
      helperText={label ? undefined : helperText}
      required={label ? undefined : required}
      fullWidth={Boolean(label) || fullWidth}
      sx={label ? undefined : sx}
      {...props}
    />
  );
  return withExternalLabel({ label, required, error, helperText, sx, children: field });
}

// Styled select — thin wrapper over the shared IpdTextField's select branch.
export function InvSelect({
  label,
  value,
  onChange,
  options = [],
  name,
  InputLabelProps,
  error,
  helperText,
  required,
  sx,
  fullWidth,
  ...props
}) {
  const hasEmptyOption = options.some(opt => (opt.value ?? opt) === '');
  const field = (
    <IpdTextField
      select
      name={name}
      value={value || ''}
      onChange={onChange}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={label ? undefined : InputLabelProps}
      error={Boolean(error)}
      helperText={label ? undefined : helperText}
      required={label ? undefined : required}
      fullWidth={Boolean(label) || fullWidth}
      sx={label ? undefined : sx}
      {...props}
    >
      {!hasEmptyOption && (
        <MenuItem value=""><em style={{ color: IPD_COLORS.textSecondary }}>— Select —</em></MenuItem>
      )}
      {options.map(opt => (
        <MenuItem key={opt.value ?? opt} value={opt.value ?? opt}>
          {opt.label ?? opt}
        </MenuItem>
      ))}
    </IpdTextField>
  );
  return withExternalLabel({ label, required, error, helperText, sx, children: field });
}
